import { useState, useEffect, useRef } from "react";
import { API, getAuthHeaders } from "../../App";
import { Button } from "@/components/ui/button";
import { FileText, Box, Eye, Download, Loader2, X } from "lucide-react";

const CAD_2D = new Set([".dwg", ".dxf", ".dxb"]);
const CAD_3D = new Set([".step", ".stp", ".iges", ".igs", ".stl", ".obj", ".gltf", ".glb", ".fbx", ".3ds"]);
const CAD_NATIVE = new Set([".sldprt", ".sldasm", ".slddrw", ".ipt", ".iam", ".idw", ".3dm", ".sat", ".x_t", ".x_b", ".prt", ".asm"]);

function getFileExt(filename) {
  return (filename || "").substring((filename || "").lastIndexOf(".")).toLowerCase();
}

export function isCADFile(filename) {
  const ext = getFileExt(filename);
  return CAD_2D.has(ext) || CAD_3D.has(ext) || CAD_NATIVE.has(ext);
}

export function CADFormatBadge({ filename }) {
  const ext = getFileExt(filename);
  const is2D = CAD_2D.has(ext);
  const is3D = CAD_3D.has(ext);
  const color = is3D ? "#8B5CF6" : is2D ? "#2563EB" : "#64748B";
  const label = ext.replace(".", "").toUpperCase();
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}
      data-testid="cad-format-badge"
    >
      {is3D ? <Box size={10} /> : <FileText size={10} />}
      {label}
    </span>
  );
}

export function CADPreviewButton({ blueprintId, filename, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const ext = getFileExt(filename);
  const hasPreview = ext === ".dxf" || CAD_3D.has(ext);

  if (!isCADFile(filename)) return null;

  return (
    <>
      <Button
        size={size}
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-none border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white"
        data-testid={`cad-preview-btn-${blueprintId}`}
      >
        <Eye size={14} className="mr-1" />
        {hasPreview ? "Preview" : "Info"}
      </Button>
      {open && (
        <CADPreviewModal
          blueprintId={blueprintId}
          filename={filename}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CADPreviewModal({ blueprintId, filename, onClose }) {
  const [loading, setLoading] = useState(true);
  const [svgContent, setSvgContent] = useState(null);
  const [error, setError] = useState(null);
  const ext = getFileExt(filename);
  const is3D = CAD_3D.has(ext);

  useEffect(() => {
    if (ext === ".dxf") {
      fetch(`${API}/preview/cad/${blueprintId}`, {
        headers: getAuthHeaders(),
        credentials: "include",
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.text();
        })
        .then((svg) => {
          setSvgContent(svg);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [blueprintId, ext]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="cad-preview-modal">
      <div
        className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] bg-[#0F172A]">
          <div className="flex items-center gap-3">
            <CADFormatBadge filename={filename} />
            <span className="text-white text-sm font-mono truncate">{filename}</span>
          </div>
          <button onClick={onClose} className="text-white hover:text-[#DC2626] transition-colors" data-testid="close-cad-preview">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-[#FAFAFA] flex items-center justify-center min-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-[#64748B]">
              <Loader2 size={32} className="animate-spin" />
              <span className="text-sm">Generating preview...</span>
            </div>
          ) : error ? (
            <div className="text-center p-8">
              <FileText size={40} className="mx-auto text-[#94A3B8] mb-3" />
              <p className="text-[#64748B] text-sm">Preview unavailable</p>
              <p className="text-[#94A3B8] text-xs mt-1">{error}</p>
              <p className="text-[#94A3B8] text-xs mt-2">Upload the DXF file content to enable preview generation.</p>
            </div>
          ) : svgContent ? (
            <div
              className="w-full h-full p-4 overflow-auto"
              dangerouslySetInnerHTML={{ __html: svgContent }}
              data-testid="cad-svg-preview"
              style={{ minHeight: 300 }}
            />
          ) : is3D ? (
            <CAD3DViewer filename={filename} blueprintId={blueprintId} />
          ) : ext === ".dwg" ? (
            <div className="text-center p-8">
              <FileText size={48} className="mx-auto text-[#1E40AF] mb-3" />
              <p className="font-bold text-[#0F172A]">AutoCAD DWG File</p>
              <p className="text-[#64748B] text-sm mt-2 max-w-md mx-auto">
                DWG is a proprietary binary format. To generate an in-browser preview,
                export as DXF from AutoCAD and re-upload.
              </p>
            </div>
          ) : (
            <div className="text-center p-8">
              <Box size={48} className="mx-auto text-[#64748B] mb-3" />
              <p className="font-bold text-[#0F172A]">{ext.replace(".", "").toUpperCase()} File</p>
              <p className="text-[#64748B] text-sm mt-2">
                This format requires a native CAD application for viewing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CAD3DViewer({ filename, blueprintId }) {
  const ext = getFileExt(filename);
  return (
    <div className="text-center p-8" data-testid="cad-3d-viewer">
      <Box size={48} className="mx-auto text-[#8B5CF6] mb-3" />
      <p className="font-bold text-[#0F172A]">3D Model — {ext.replace(".", "").toUpperCase()}</p>
      <p className="text-[#64748B] text-sm mt-2 max-w-md mx-auto">
        Formats supported: STEP, STP, IGES, STL, OBJ, GLTF, GLB, FBX, 3DS
      </p>
      <p className="text-[#94A3B8] text-xs mt-3">
        3D rendering available in the Vault desktop application.
      </p>
    </div>
  );
}
